import React, { createRef, useEffect } from "react";
import { useState } from "react";
import { connect } from "react-redux";
import * as cn from "classnames/bind";
import Button from "content/button/button";
import * as styles from "./image-uploader.css";
import { actionCreators } from "ui/store";
import { CalendarState, CustomImage, OnSaveResponse } from "burde";
import { getResource } from "resource/store";
import { ApplicationState } from "store";
import ZentaImagePicker from "content/image/zenta-image-picker";
import { getCustomImageById } from "calendar/service";

type ImageUploaderProps = {
    onSaveCallback: (
        blob: Blob,
        calendarSize: string,
    ) => Promise<OnSaveResponse>;
    onSelectCallback: (imageUrl: string | null) => Promise<OnSaveResponse>;
    calendar: CalendarState;
    Cover_Image_UploadImage: string;
    Cover_Image_EditImage: string;
    isFrontCover: boolean;
    uploadedCoverImages: Array<CustomImage>;
    onRemoveCallback: (imageUrl: string) => void;
} & typeof actionCreators;

const cx = cn.bind(styles);

const buttonCn = () =>
    cx({
        uploadButton: true,
    });

const ImageUploader: React.FC<ImageUploaderProps> = ({
    UpdateImageCropper,
    onSaveCallback,
    onSelectCallback,
    calendar,
    Cover_Image_UploadImage,
    Cover_Image_EditImage,
    isFrontCover,
    uploadedCoverImages,
    updateUploadedCoverImages,
    onRemoveCallback,
}) => {
    const fileInput = createRef<HTMLInputElement>();
    const [selectedImages, setSelectedImages] = useState<CustomImage[]>([]);
    const [selectedImagesSet, setSelectedImagesSet] = useState<boolean>(false);

    const uploadBackgroundImage = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const { files } = e.target;
        const file = files && files.length > 0 ? files[0] : null;
        if (file === null) {
            return;
        }
        const imageUrl = window.URL.createObjectURL(file);
        let image = new Image();
        image.src = imageUrl;

        if (imageUrl) {
            UpdateImageCropper({
                isVisible: true,
                imageUrl,
                onSaveCallback,
            });
        }

        e.target.value = "";
    };

    useEffect(() => {
        getAndSetSelectedImages();
    }, [calendar.customCoverImageId, calendar.customBackImageId]);

    const editUploadedImage = () => {
        const idToFind = isFrontCover
            ? calendar.customCoverImageId
            : calendar.customBackImageId;

        if (idToFind) {
            const found = uploadedCoverImages.find((x) => x.id === idToFind);

            if (found) {
                UpdateImageCropper({
                    isVisible: true,
                    imageUrl: found.url,
                    onSaveCallback,
                });
            }
        }
    };

    const getAndSetSelectedImages = async () => {
        const selectedImages: CustomImage[] = [];

        if (calendar.customCoverImageId) {
            const frontCoverImage = await getCustomImageById(
                calendar.customCoverImageId,
            );
            if (frontCoverImage) {
                selectedImages.push(frontCoverImage);
                if (
                    !uploadedCoverImages.find(
                        (x) => x.id === calendar.customCoverImageId,
                    )
                ) {
                    updateUploadedCoverImages([
                        ...uploadedCoverImages,
                        frontCoverImage,
                    ]);
                }
            }
        }

        if (calendar.customBackImageId) {
            const frontCoverImage = await getCustomImageById(
                calendar.customBackImageId,
            );
            if (frontCoverImage) {
                selectedImages.push(frontCoverImage);
                if (
                    !uploadedCoverImages.find(
                        (x) => x.id === calendar.customBackImageId,
                    )
                ) {
                    updateUploadedCoverImages([
                        ...uploadedCoverImages,
                        frontCoverImage,
                    ]);
                }
            }
        }

        setSelectedImages(selectedImages);
        setSelectedImagesSet(true);
    };

    const getSelectedImageUrl = () => {
        const idToFind = isFrontCover
            ? calendar.customCoverImageId
            : calendar.customBackImageId;

        if (idToFind) {
            const found = uploadedCoverImages.find((x) => x.id === idToFind);

            if (found) {
                return found.url;
            }
        }

        return null;
    };

    const getImagePickerImages = () => {
        const images = [...uploadedCoverImages].map((x) => x.url);
        return images;
    };

    const imageIsEditable = () => {
        const idToFind = isFrontCover
            ? calendar.customCoverImageId
            : calendar.customBackImageId;

        if (idToFind) {
            const found = uploadedCoverImages.find((x) => x.id === idToFind);

            if (found) {
                return true;
            }
        }

        return false;
    };

    return (
        <React.Fragment>
            <input
                style={{ display: "none" }}
                ref={fileInput}
                type="file"
                onChange={uploadBackgroundImage}
                accept={"image/*"}
            />

            <Button
                onClick={() => {
                    fileInput.current ? fileInput.current.click() : "";
                }}
                className={buttonCn()}
                text={Cover_Image_UploadImage}
            />
            {selectedImagesSet && (
                <ZentaImagePicker
                    selectedImage={getSelectedImageUrl()}
                    images={getImagePickerImages()}
                    defaultRoute={""}
                    onClick={(selectedImage) => {
                        onSelectCallback(selectedImage);
                    }}
                    onRemove={(selectedImage) => {
                        onRemoveCallback(selectedImage);
                    }}
                    resizeImage={false}
                    selectedImages={selectedImages}
                />
            )}

            {imageIsEditable() && (
                <Button
                    onClick={() => {
                        editUploadedImage();
                    }}
                    className={buttonCn()}
                    text={Cover_Image_EditImage}
                />
            )}
        </React.Fragment>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    Cover_Image_UploadImage: getResource(state, {
        key: "Cover_Image_UploadImage",
    }),
    Cover_Image_EditImage: getResource(state, {
        key: "Cover_Image_EditImage",
    }),
});

const mapDispatchToProps = {
    ...actionCreators,
};

const ConnectedImageUploader = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ImageUploader);

export { ConnectedImageUploader };
